import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { getAllWithoutPagination } from "src/app/utils/graphql";
import {
  EditFeaturedElementsGQL,
  FeaturedElementFragment,
  FeaturedElementsGQL,
  GetElementIfExistsGQL,
} from '@/core/graphql/graphql';

@Injectable({
  providedIn: 'root',
})
export class FeaturedElementsDeprecatedService {
  private readonly _loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly _loadingObservable$: Observable<boolean> = this._loadingSubject.asObservable();

  private readonly _elementsSubject: BehaviorSubject<FeaturedElementFragment[]> = new BehaviorSubject<FeaturedElementFragment[]>([]);
  private readonly _elements$: Observable<FeaturedElementFragment[]> = this._elementsSubject.asObservable();

  private _elementsWereFetched = false;

  public constructor(
    private readonly _featuredElementsGql: FeaturedElementsGQL,
    private readonly _editFeaturedElementsGql: EditFeaturedElementsGQL,
    private readonly _getElementIfExistsGql: GetElementIfExistsGQL,
  ) { }

  public getFeaturedElements(): Observable<FeaturedElementFragment[]> {
    if (this._elementsWereFetched) {
      return this._elements$;
    }

    return from(this.fetchElements()).pipe(
      tap((elements) => {
        this._elementsSubject.next(elements);
        this._elementsWereFetched = true;
      }),
      switchMap(() => this._elements$),
    );
  }

  public updateFeaturedIds(values: number[]): void {
    this._loadingSubject.next(true);

    this._editFeaturedElementsGql.mutate({ input: { elementIds: values } })
      .toPromise()
      .then((elements) => {
        this._loadingSubject.next(false);
        this._elementsSubject.next(elements.data?.element?.editFeatured?.items ?? []);
      });
  }

  public addFeaturedElement(id: number): void {
    const ids: number[] = this._elementsSubject.value.map(element => element.id);
    if (!ids.includes(id)) {
      ids.unshift(id);
      this.updateFeaturedIds(ids);
    }
  }

  public removeFeaturedElement(id: number): void {
    const ids: number[] = this._elementsSubject.value.map(element => element.id);
    const index = ids.findIndex(next => next === id);
    ids.splice(index, 1);
    this.updateFeaturedIds(ids);
  }

  private async fetchElements(): Promise<FeaturedElementFragment[]> {
    this._loadingSubject.next(true);

    const result = await getAllWithoutPagination(
      (x) => this._featuredElementsGql
        .fetch({ meta: { pageCursor: x, pageSize: 199 } })
        .toPromise(), (res) => res.data.elements,
    );

    this._loadingSubject.next(false);
    return result;
  }

  public getElementIfExists(id: number): Observable<FeaturedElementFragment | null> {
    this._loadingSubject.next(true);

    return this._getElementIfExistsGql.fetch({ id })
      .pipe(
        map((x) => {
          this._loadingSubject.next(false);
          return x.data.element;
        }),
        catchError(err => {
          this._loadingSubject.next(false);
          return of(null);
        }),
      );
  }
}
